import {
  createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { AWAY_WINDOW_CHANGED_EVENT, getMyAway, type AwayWindow } from '../api/away';
import { useAuth } from './AuthContext';

type DndQuietModeApi = {
  isDndQuiet: boolean;
  refreshDndQuietMode: () => Promise<boolean>;
};

const DEFAULT_VALUE: DndQuietModeApi = {
  isDndQuiet: false,
  refreshDndQuietMode: async () => false,
};

const DndQuietModeContext = createContext<DndQuietModeApi>(DEFAULT_VALUE);

export function isActiveDndWindow(window: AwayWindow | null, now = Date.now()): boolean {
  if (!window || window.status_type !== 'dnd' || window.ended_at) return false;
  const startsAt = Date.parse(window.starts_at);
  const endsAt = Date.parse(window.ends_at);
  return Number.isFinite(startsAt) && Number.isFinite(endsAt) && startsAt <= now && now < endsAt;
}

/**
 * A client-side fail-safe for a real DND window. The server remains
 * authoritative for delivery, while this prevents any locally delivered frame
 * from creating sound, a native alert, or an incoming-call screen.
 */
export function DndQuietModeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isDndQuiet, setIsDndQuiet] = useState(false);

  const refreshDndQuietMode = useCallback(async (): Promise<boolean> => {
    if (!user?.id) {
      setIsDndQuiet(false);
      return false;
    }
    try {
      const window = await getMyAway();
      const next = isActiveDndWindow(window);
      setIsDndQuiet(next);
      return next;
    } catch {
      // Server-side call and mention guards remain the source of truth when a
      // refresh is temporarily unavailable.
      setIsDndQuiet(false);
      return false;
    }
  }, [user?.id]);

  useEffect(() => {
    void refreshDndQuietMode();
    const intervalId = window.setInterval(() => { void refreshDndQuietMode(); }, 30_000);
    const onFocus = () => { void refreshDndQuietMode(); };
    const onAwayWindowChange = () => { void refreshDndQuietMode(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener(AWAY_WINDOW_CHANGED_EVENT, onAwayWindowChange);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(AWAY_WINDOW_CHANGED_EVENT, onAwayWindowChange);
    };
  }, [refreshDndQuietMode]);

  const value = useMemo(() => ({ isDndQuiet, refreshDndQuietMode }), [isDndQuiet, refreshDndQuietMode]);
  return <DndQuietModeContext.Provider value={value}>{children}</DndQuietModeContext.Provider>;
}

export function useDndQuietMode(): DndQuietModeApi {
  return useContext(DndQuietModeContext);
}
